import type { IndicatorResult } from "./types";

const pct = (v: number | null) => (v == null ? "-" : `${v.toFixed(1)}%`);

/** Join a list of names naturally: "A", "A and B", "A, B and C". */
function joinNames(xs: string[]) {
  if (xs.length <= 1) return xs[0] ?? "";
  if (xs.length === 2) return `${xs[0]} and ${xs[1]}`;
  return `${xs.slice(0, -1).join(", ")} and ${xs[xs.length - 1]}`;
}

const EPS = 1e-9;

/** All items whose numeric value equals the extreme (max or min). Ties are reported
 *  together instead of arbitrarily picking the first. */
function extremesBy<T>(items: T[], value: (t: T) => number) {
  const vals = items.map(value);
  const hi = Math.max(...vals);
  const lo = Math.min(...vals);
  return {
    hi,
    lo,
    hiItems: items.filter((t) => Math.abs(value(t) - hi) < EPS),
    loItems: items.filter((t) => Math.abs(value(t) - lo) < EPS),
  };
}

/** Smart narrative computed from the indicator set: headline insight, strongest and
 *  weakest coverage, a safe descriptive comparison, and how many figures are official.
 *  `group` names the population slice ("Men", "Women", "Under 18", ...) when one is
 *  selected, so the narrative frames itself correctly. When it is empty, the story is
 *  the general one for everyone. Only descriptive statements are made: highest, lowest,
 *  average, spread, counts. No trend, inference, or causation. Ties are reported in
 *  full, not collapsed to a single name. */
export function dashboardNarrative(rows: IndicatorResult[], cycle: number, group = "") {
  const withVals = rows.filter((r) => r.value != null);
  const who = group ? group.toLowerCase() : "";
  const forWhom = who ? ` for ${who}` : "";
  const amongWhom = who ? `Among ${who}, ` : "";
  if (withVals.length === 0) {
    return {
      headline: group
        ? `No indicators have been reported${forWhom} in ${cycle} yet.`
        : `No indicators are available for ${cycle} yet.`,
      points: [] as string[],
    };
  }
  const { hi, lo, hiItems, loItems } = extremesBy(withVals, (r) => Number(r.value ?? 0));
  const official = rows.filter((r) => r.status === "official").length;
  const reported = withVals.length;
  const totalInd = rows.length;
  const avg = withVals.reduce((s, r) => s + (r.value ?? 0), 0) / reported;
  const spread = hi - lo;
  const tied = spread < EPS;                    // every reported value is the same
  const hiNames = hiItems.map((r) => r.name);
  const loNames = loItems.map((r) => r.name);

  const points: string[] = [];
  points.push(hiItems.length > 1
    ? `${amongWhom}strongest coverage (tied): ${joinNames(hiNames)} at ${pct(hi)}.`
    : `${amongWhom}strongest coverage: ${hiItems[0].name} at ${pct(hi)}.`);
  if (!tied) {
    points.push(loItems.length > 1
      ? `Lowest coverage (tied): ${joinNames(loNames)} at ${pct(lo)}, the widest gap to close.`
      : `Lowest coverage: ${loItems[0].name} at ${pct(lo)}, the widest gap to close.`);
    points.push(`Reported coverage${forWhom} ranges ${spread.toFixed(1)} percentage points, from ${pct(lo)} to ${pct(hi)}.`);
  } else if (reported > 1) {
    points.push(`All ${reported} reported indicators${forWhom} share the same value, ${pct(hi)}.`);
  }
  points.push(`Average across the ${reported} reported indicator${reported > 1 ? "s" : ""}${forWhom} is ${avg.toFixed(1)}%.`);
  if (reported < totalInd)
    points.push(`${reported} of ${totalInd} indicators have a figure${forWhom}; the rest are awaiting data.`);
  points.push(official === totalInd
    ? `All ${totalInd} indicators are official for ${cycle}.`
    : `${official} of ${totalInd} indicators are official; the rest are provisional.`);
  const flagged = rows.filter((r) => (r.flags ?? []).length > 0).length;
  if (flagged) points.push(`${flagged} indicator${flagged > 1 ? "s carry" : " carries"} a data-quality flag for review.`);

  const leadNames = joinNames(hiNames);
  const headline = group
    ? `${amongWhom}reported coverage averages ${avg.toFixed(1)}% in ${cycle}, led by ${leadNames} (${pct(hi)}).`
    : `In ${cycle}, reported social protection coverage averages ${avg.toFixed(1)}%, led by ${leadNames} (${pct(hi)}).`;
  return { headline, points };
}

/** Short, reliable auto-summaries for the scrolling ticker. */
export function tickerSummaries(rows: IndicatorResult[], cycle: number): string[] {
  const out: string[] = [];
  const vals = rows.filter((r) => r.value != null);
  if (vals.length) {
    const { hi, hiItems } = extremesBy(vals, (r) => Number(r.value ?? 0));
    const names = joinNames(hiItems.map((r) => r.name));
    out.push(hiItems.length > 1
      ? `${cycle}: highest coverage (tied) - ${names} at ${hi.toFixed(1)}%.`
      : `${cycle}: highest coverage - ${hiItems[0].name} at ${hi.toFixed(1)}%.`);
    const prov = rows.filter((r) => r.status === "provisional").length;
    if (prov) out.push(`${prov} indicator${prov > 1 ? "s" : ""} still provisional for ${cycle}.`);
  }
  return out;
}

/* ------------------------------------------------------------------ */
/*  Scheme & benefit narratives (descriptive only - counts, shares,   */
/*  mode; no inference, trend, or causation). Ties reported in full.  */
/* ------------------------------------------------------------------ */

type Dist = { label: string; count: number }[];
type SchemeStats = {
  total: number; legally_anchored_pct: number | null; non_contributory_pct: number | null;
  by_contributory: Dist; by_programme_type: Dist; by_agency: Dist; by_legal_status: Dist;
};
type BenefitStats = {
  total: number; cash_pct: number | null; non_contributory_pct: number | null;
  by_function: Dist; by_cash_kind: Dist; by_contributory: Dist; by_targeting: Dist;
};

const share = (n: number, total: number) => (total ? Math.round((n / total) * 100) : 0);
const findLabel = (d: Dist, label: string) => d?.find((x) => x.label.toLowerCase() === label.toLowerCase())?.count ?? 0;

/** The most common category (or categories, if tied) in a distribution. Returns the
 *  shared count and every label at that count, so a tie names all of them instead of
 *  silently picking one. */
function topLabels(d: Dist): { count: number; labels: string[] } | null {
  if (!d || !d.length) return null;
  const max = Math.max(...d.map((x) => x.count));
  return { count: max, labels: d.filter((x) => x.count === max).map((x) => x.label) };
}

export function schemeNarrative(s: SchemeStats, agencies: number, cycle: number) {
  if (!s.total) return { headline: `No schemes are in the register for ${cycle} yet.`, points: [] as string[] };
  const points: string[] = [];
  const contrib = findLabel(s.by_contributory, "Contributory");
  const nonContrib = findLabel(s.by_contributory, "Non-contributory");
  points.push(`${s.total} scheme${s.total > 1 ? "s" : ""} recorded across ${agencies} administering ${agencies > 1 ? "bodies" : "body"} for ${cycle}.`);
  if (contrib || nonContrib)
    points.push(`Of these, ${contrib} ${contrib === 1 ? "is" : "are"} contributory and ${nonContrib} non-contributory (${share(nonContrib, s.total)}% non-contributory).`);
  if (s.legally_anchored_pct != null)
    points.push(`${s.legally_anchored_pct}% are anchored in national legislation.`);
  const pt = topLabels(s.by_programme_type);
  if (pt) {
    points.push(pt.labels.length > 1
      ? `The most common programme types are tied: ${joinNames(pt.labels)} (${pt.count} each of ${s.total}).`
      : `The most common programme type is ${pt.labels[0]} (${pt.count} of ${s.total}).`);
  }
  const headline = `In ${cycle}, ${s.total} social-protection scheme${s.total > 1 ? "s are" : " is"} on record, ${
    s.non_contributory_pct != null ? `${s.non_contributory_pct}% non-contributory` : "spanning several programme types"
  }.`;
  return { headline, points };
}

export function benefitNarrative(b: BenefitStats, cycle: number) {
  if (!b.total) return { headline: `No benefits are in the register for ${cycle} yet.`, points: [] as string[] };
  const points: string[] = [];
  const cash = findLabel(b.by_cash_kind, "In cash");
  const kind = findLabel(b.by_cash_kind, "In kind");
  points.push(`${b.total} benefit${b.total > 1 ? "s" : ""} recorded for ${cycle}.`);
  if (cash || kind)
    points.push(`${cash} ${cash === 1 ? "is" : "are"} paid in cash and ${kind} in kind (${share(cash, b.total)}% cash).`);
  if (b.non_contributory_pct != null)
    points.push(`${b.non_contributory_pct}% are non-contributory.`);
  const fn = topLabels(b.by_function);
  if (fn) {
    points.push(fn.labels.length > 1
      ? `The most common benefit functions are tied: ${joinNames(fn.labels)} (${fn.count} each of ${b.total}).`
      : `The most common benefit function is ${fn.labels[0]} (${fn.count} of ${b.total}).`);
  }
  const headline = `In ${cycle}, ${b.total} benefit${b.total > 1 ? "s are" : " is"} on record${
    b.cash_pct != null ? `, ${b.cash_pct}% paid in cash` : ""
  }.`;
  return { headline, points };
}
